import { HTMLDOMProperties, SVGDOMProperties } from 'common/constants/domProperties';
import eventTypes from 'common/constants/eventTypes';
import isCustomAttribute from 'utils/dom/isCustomAttribute';

const EVENT_PROP_REGEX = /^on([A-Z])([a-zA-Z]+?)(Capture)?$/;

/**
 * Converts the key of an event prop to the key in React's supported event types
 *
 * For example, "onMouseEnterCapture" becomes "mouseEnter"
 *
 * @param  {String} eventPropKey - key to convert
 * @return {String} event type
 */
export const eventPropToType = (eventPropKey) => {
    const matches = EVENT_PROP_REGEX.exec(eventPropKey);
    if (matches) {
        return `${matches[1].toLowerCase()}${matches[2]}`;
    }
    return eventPropKey;
};

/**
 * Determines whether a property key is an HTML attribute supported by React
 *
 * @param  {String} propKey - key to validate
 * @return {bool} whether the attribute is supported
 */
export const isSupportedHtmlAttribute = (propKey, allowCustom = true) =>
    propKey in HTMLDOMProperties || (allowCustom && isCustomAttribute(propKey));

/**
 * Determines whether a property key is an SVG attribute supported by React
 *
 * @param  {String} propKey - key to validate
 * @return {bool} whether the attribute is supported
 */
export const isSupportedSvgAttribute = (propKey, allowHtml = true) =>
    propKey in SVGDOMProperties || (allowHtml && propKey in HTMLDOMProperties);

/**
 * Determines whether a property key is a supported React event
 *
 * @param  {String} propKey - key to validate
 * @return {bool} whether the event is supported
 */
export const isSupportedEvent = (propKey) => {
    if (propKey.match(EVENT_PROP_REGEX)) {
        return eventPropToType(propKey) in eventTypes;
    }
    return false;
};

/**
 * Filters an object so that it only contains keys that are valid attributes for HTML elements
 *
 * @param  {Object} props - mapping of props to values
 * @return {Object} filtered props
 */
export const filterHtmlAttributesOnly = (props) => {
    const filteredProps = {};
    Object.keys(props).forEach((propKey) => {
        if (isSupportedHtmlAttribute(propKey)) {
            filteredProps[propKey] = props[propKey];
        }
    });
    return filteredProps;
};

/**
 * Filters an object so that it only contains keys that are valid attributes for SVG elements
 *
 * @param  {Object} props - mapping of props to values
 * @return {Object} filtered props
 */
export const filterSvgAttributesOnly = (props) => {
    const filteredProps = {};
    Object.keys(props).forEach((propKey) => {
        if (isSupportedSvgAttribute(propKey)) {
            filteredProps[propKey] = props[propKey];
        }
    });
    return filteredProps;
};

/**
 * Filters an object so that it only contains keys that are valid event listeners
 *
 * @param  {Object} props - mapping of props to values
 * @return {Object} filtered props
 */
export const filterSupportedEventsOnly = (props) => {
    const filteredProps = {};
    Object.keys(props).forEach((propKey) => {
        if (isSupportedEvent(propKey)) {
            filteredProps[propKey] = props[propKey];
        }
    });
    return filteredProps;
};

/**
 * Filters an object so that it only contains keys that are valid props for basic HTML elements
 *
 * @param  {Object} props - mapping of props to values
 * @return {Object} filtered props
 */
const filterBasicPropsOnly = (props) => {
    const filteredProps = {};
    Object.keys(props).forEach((propKey) => {
        if (
            isSupportedHtmlAttribute(propKey, true) ||
            isSupportedSvgAttribute(propKey, false) ||
            isSupportedEvent(propKey)
        ) {
            filteredProps[propKey] = props[propKey];
        }
    });
    return filteredProps;
};

export default filterBasicPropsOnly;
